import {useState} from "react";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";
import {Grid} from "@mui/material";
import {Dialog} from "../base/Dialog";
import {UploadDataType, UploadDataView} from "../../types/uploadDataEnums";
import {DataUploadOptionsView} from "./dataUpload/OptionsView";
import {DataUploadIrrigationRainfallView} from "./dataUpload/IrrigationRainfallView";
import {AppliedIrrigationData} from "../../types/domain/appliedIrrigation";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {bulkCreateAppliedIrrigation} from "../../services/appliedIrrigationService";
import {swallowException} from "../../utils/appUtils";
import {CloudUploadIcon} from "../icons/CloudUploadIcon";
import {RainfallIrrigationCsvUpload} from "./RainfallIrrigationCsvUpload";
import {useCompany} from "../../hooks/companiesHooks";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const DataUploadDialog: React.FC<Props> = ({open, onClose}) => {
    const {t} = useTranslation();
    const company = useCompany();
    const [dataType, setDataType] = useState(UploadDataType.IRRIGATION_RAINFALL);
    const [view, setView] = useState(UploadDataView.OPTIONS);
    const [submittable, setSubmittable] = useState(false);
    const [fieldsData, setFieldsData] = useState<AppliedIrrigationData>({
        companyId: "",
        date: dayjs().format("YYYY-MM-DD"),
        fields: [],
    });
    const [clear, setClear] = useState(false);
    const appliedIrrigationManagement = useResourceManagement({
        bulkCreateAppliedIrrigation,
    });
    const error = undefined;

    const onSubmit = () => {
        if (view === UploadDataView.OPTIONS) {
            if (dataType === UploadDataType.IRRIGATION_RAINFALL) {
                setView(UploadDataView.IRRIGATION_RAINFALL);
                return;
            }
            if (dataType === UploadDataType.CSV_UPLOAD) {
                setView(UploadDataView.CSV_UPLOAD);
                return;
            }
        }
        if (view === UploadDataView.IRRIGATION_RAINFALL && submittable) {
            appliedIrrigationManagement.reset();
            appliedIrrigationManagement.bulkCreateAppliedIrrigation(fieldsData).catch(swallowException);
        }

        if (view === UploadDataView.CSV_UPLOAD) {
            onClose(true);
        }
    };

    return (
        <Dialog
            width="50rem"
            enableSubmit
            title={t("dialogs.data_upload.title")}
            titleIcon={
                <CloudUploadIcon
                    sx={{
                        stroke: "#404DB8",
                        width: "1.25rem",
                        height: "1.25rem",
                    }}
                />
            }
            open={open}
            error={error}
            onClose={() => onClose(false)}
            onReset={() => setClear(true)}
            enableReset={view === UploadDataView.IRRIGATION_RAINFALL}
            submitTitle={
                view === UploadDataView.OPTIONS
                    ? t("dialogs.actions.next")
                    : view === UploadDataView.CSV_UPLOAD
                      ? t("dialogs.actions.done")
                      : t("dialogs.actions.submit")
            }
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={onSubmit}
            shouldCloseOnSubmit={submittable}
        >
            <Grid container spacing={2}>
                {view === UploadDataView.OPTIONS && (
                    <DataUploadOptionsView dataType={dataType} setDataType={setDataType} />
                )}
                {view === UploadDataView.IRRIGATION_RAINFALL && (
                    <DataUploadIrrigationRainfallView
                        setSubmittable={setSubmittable}
                        setFieldsData={setFieldsData}
                        clear={clear}
                        setClear={setClear}
                    />
                )}
                {view === UploadDataView.CSV_UPLOAD && company?.data && (
                    <RainfallIrrigationCsvUpload companyId={company?.data?.id} />
                )}
            </Grid>
        </Dialog>
    );
};
